/**
 * Customer metrics calculation utilities
 * 
 * These functions calculate business metrics from customer data,
 * keeping calculation logic separate from UI components.
 */

/**
 * Calculates customer metrics from customer list
 * @param {Array} customers - Array of customers
 * @returns {Object} Metrics object with totalCustomers, newCustomersThisMonth, activeCustomers, inactiveCustomers
 */
export const calculateCustomerMetrics = (customers) => {
    // Calculate total unique customers by email
    const uniqueEmails = new Set(
        customers
            .map((p) => (p.customer_email || '').trim().toLowerCase())
            .filter(Boolean)
    );

    // Create map of unique customers by wallet_pass_id
    const uniqueCustomersMap = new Map();
    customers.forEach((p) => {
        uniqueCustomersMap.set(p.wallet_pass_id, p);
    });

    const allUniqueCustomers = Array.from(uniqueCustomersMap.values());

    // Calculate active/inactive customers based on 30-day threshold
    const now = new Date();
    const activityThresholdDays = 30;
    const activityThresholdDate = new Date(
        Date.UTC(
            now.getUTCFullYear(),
            now.getUTCMonth(),
            now.getUTCDate() - activityThresholdDays
        )
    );

    const activeCustomers = allUniqueCustomers.filter((p) => {
        if (!p.updated_at) {
            return false;
        }
        return new Date(p.updated_at) >= activityThresholdDate;
    }).length;

    const inactiveCustomers = allUniqueCustomers.filter((p) => {
        if (!p.updated_at) {
            return true;
        }
        return new Date(p.updated_at) < activityThresholdDate;
    }).length;

    // Calculate new customers this month
    const currentMonth = now.getUTCMonth();
    const currentYear = now.getUTCFullYear();
    const passesCreatedThisMonth = customers.filter((p) => {
        if (!p.customer_created_at) {
            return false;
        }
        const d = new Date(p.customer_created_at);
        return d.getUTCFullYear() === currentYear && d.getUTCMonth() === currentMonth;
    });

    const newCustomersThisMonth = new Set(
        passesCreatedThisMonth.map((p) => p.merchant_customer_id).filter(Boolean)
    ).size;

    return {
        totalCustomers: uniqueEmails.size,
        newCustomersThisMonth,
        activeCustomers,
        inactiveCustomers,
    };
};

